import React, { useState } from "react";
import { useAuth } from '@chainlit/react-client';
import Page from 'pages/Page';
import { sendFeedback, fetchFeedbacks, type FeedbackItem } from 'api/feedback'; // Adjust path if needed

export default function Feedback() {
  const [feature, setFeature] = useState(""); // Start empty
  const [feedback, setFeedback] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedbacks, setFeedbacks] = useState<FeedbackItem[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const { user } = useAuth();
  const userEmail = (user?.metadata as any)?.email || '';
  const name = (user?.metadata as any)?.name || '';
  const userRole = (user?.metadata as any)?.role || ''; 

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!feature) {
      setError("Please select a feature.");
      return;
    }
    if (!feedback.trim()) {
      setError("Feedback is required.");
      return;
    }

    try {
      await sendFeedback(userEmail, feature, feedback);
      setSubmitted(true);
      setFeedback(""); // Reset feedback field
      setFeature(""); // Reset feature dropdown
      setTimeout(() => setSubmitted(false), 3000); // Hide success after 3s
    } catch (err: any) {
      setError("Failed to send feedback. Please try again.");
    }
  };

  const handleFetchAll = async () => {
    setListError(null);
    setLoadingList(true);
    try {
      const res = await fetchFeedbacks();
      setFeedbacks(res.feedbacks || []);
    } catch (e: any) {
      setListError(e.message || 'Failed to fetch feedbacks');
    } finally {
      setLoadingList(false);
    }
  };

  return (
    <Page>
      <div className="w-full mt-10 p-8 bg-background/60 rounded-xl border border-border/30 shadow">
        <h2 className="text-xl font-bold mb-4">Feedback Form</h2>

        {/* Admin-only: fetch all feedbacks */}
      

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <input
              type="text"
              value={name || ""}
              disabled
              className="w-full px-3 py-2 rounded border border-border/40 bg-background/30 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Email</label>
            <input
              type="email"
              value={userEmail || ""}
              disabled
              className="w-full px-3 py-2 rounded border border-border/40 bg-background/30 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Feature <span className="text-red-500">*</span></label>
            <select
              value={feature}
              onChange={e => setFeature(e.target.value)}
              className="w-full px-3 py-2 rounded border border-border/40 bg-background/30 text-sm"
              required
            >
              <option value="" disabled>Select a feature</option>
              <option value="askawatch">AskAWatch</option>
              <option value="changevalidation">Change Validation</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Feedback <span className="text-red-500">*</span></label>
            <textarea
              value={feedback}
              onChange={e => setFeedback(e.target.value)}
              rows={5}
              className="w-full px-3 py-2 rounded border border-border/40 bg-background/30 text-sm resize-vertical"
              required
            />
          </div>
          <button
            type="submit"
            className="bg-[#18E78F] text-white px-4 py-2 rounded font-semibold hover:bg-[#18E78F]/80 transition"
            disabled={submitted}
          >
            Submit
          </button>
          {submitted && (
            <div className="text-green-600 text-sm mt-2">Thank you for your feedback!</div>
          )}
          {error && (
            <div className="text-red-600 text-sm mt-2">{error}</div>
          )}
        </form>

      

        {/* Admin-only list render */}
        {userRole === 'admin' && (<>
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
          <button
            onClick={handleFetchAll}
            className="inline-flex items-center gap-2 bg-[#003F2D] text-white px-3 py-2 rounded-md border border-[#18E78F]/50 hover:bg-[#003F2D]/90 shadow-sm text-sm"
            disabled={loadingList}
            title="Fetch all feedbacks"
          >
            {loadingList ? (
              <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z" />
              </svg>
            ) : (
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 5h18v2H3V5zm0 6h18v2H3v-2zm0 6h18v2H3v-2z" />
              </svg>
            )}
            <span>{loadingList ? 'Loading…' : 'Fetch Feedbacks'}</span>
            {feedbacks.length > 0 && !loadingList && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-[#18E78F]/20 text-[#18E78F] text-[11px] border border-[#18E78F]/40">
                {feedbacks.length}
              </span>
            )}
          </button>
          {listError && <span className="text-red-600 text-xs">{listError}</span>}
        </div>
        </div>
        

          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold">All Feedbacks</h3>
              {feedbacks.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">
                    {feedbacks.length} items
                  </span>
                  <button
                    onClick={handleFetchAll}
                    className="text-xs px-2 py-1 rounded border border-border/40 bg-background/40 hover:bg-background/60"
                    disabled={loadingList}
                    title="Refresh"
                  >
                    Refresh
                  </button>
                </div>
              )}
            </div>

            {/* Empty state */}
            {feedbacks.length === 0 && !loadingList && !listError && (
              <div className="text-[12px] text-muted-foreground border border-dashed border-border/40 rounded-md px-3 py-4">
                No feedbacks yet. Click “Fetch Feedbacks” to load.
              </div>
            )}

            {/* List */}
            {feedbacks.length > 0 && (
              <div className="border border-border/30 rounded overflow-hidden">
                <div className="grid grid-cols-3 gap-2 px-3 py-2 bg-background/50 text-[11px] font-medium border-b border-border/30">
                  <span>Email</span>
                  <span>Feature</span>
                  <span>Submitted • Feedback</span>
                </div>
                <div className="divide-y divide-border/20">
                  {(feedbacks ?? [])
                    .slice()
                    .sort((a, b) => {
                      const sa = a?.submitted_at ?? "";
                      const sb = b?.submitted_at ?? "";
                      // Prefer date compare; fallback to string
                      const da = sa ? new Date(sa).getTime() : 0;
                      const db = sb ? new Date(sb).getTime() : 0;
                      return db - da || sb.localeCompare(sa);
                    })
                    .map((f, idx) => (
                      <div
                        key={f.id ?? `${idx}`}
                        className={`grid grid-cols-3 gap-2 px-3 py-2 text-[12px] ${
                          idx % 2 === 0 ? 'bg-background/30' : 'bg-background/50'
                        }`}
                      >
                        <span className="truncate">{f.email ?? '-'}</span>
                        <span className="truncate capitalize">{f.feature ?? '-'}</span>
                        <div className="flex flex-col">
                          <span className="text-muted-foreground text-[11px]">{f.submitted_at ?? '-'}</span>
                          <span className="text-[12px]">{f.feedback_text ?? '-'}</span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </>)}
      </div>
    </Page>
  );
}